import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import apiClient from '../../../../../../api/apiClient';
import { usePreloaderStore, useConfirmationModalStore } from '../../../../../../stores';
import Modal from '../../../controls/Modal';
import LabeledInput from '../../../controls/LabeledInput';

const FIELDS = [
    { key: 'balance', label: 'Баланс (текущие баллы)', step: '1' },
    { key: 'totalBalance', label: 'Всего заработано (влияет на рейтинг)', step: '1' },
    { key: 'coinBalance', label: 'Монеты (SolCoin)', step: 'any' }
];

const toStr = (v) => (v == null ? '' : String(v));
const isValid = (s) => s.trim() !== '' && !Number.isNaN(Number(s));

// Модалка правки балансов игрока (Насоли соседу). PATCH перезаписывает значения (не инкремент),
// шлём только изменённые поля. Действие необратимо и не журналируется — с подтверждением.
export default function BalanceEditModal({ stateId, values, onClose, onSaved }) {
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const { open: openConfirm } = useConfirmationModalStore();

    const [form, setForm] = useState({
        balance: toStr(values.balance),
        totalBalance: toStr(values.totalBalance),
        coinBalance: toStr(values.coinBalance)
    });

    const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

    const allValid = FIELDS.every(f => isValid(form[f.key]));
    const changed = FIELDS.filter(f => isValid(form[f.key]) && Number(form[f.key]) !== Number(values[f.key] ?? 0));

    const doSave = async () => {
        const payload = {};
        changed.forEach(f => { payload[f.key] = Number(form[f.key]); });
        show();
        try {
            const { data } = await apiClient.patch(`/api/neighbor/admin/neighbor-student-states/update?id=${stateId}`, payload);
            enqueueSnackbar('Балансы обновлены', { variant: 'success' });
            onSaved({
                balance: data?.balance ?? Number(form.balance),
                totalBalance: data?.totalBalance ?? Number(form.totalBalance),
                coinBalance: data?.coinBalance ?? Number(form.coinBalance)
            });
            onClose();
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Ошибка при обновлении балансов', { variant: 'error' });
        } finally { hide(); }
    };

    const save = () => {
        if (!allValid || changed.length === 0) return;
        openConfirm(
            <div className="confirm-msg">
                <b>Изменить балансы игрока?</b>
                <p>
                    {changed.map(f => (
                        <span key={f.key}>{f.label.split(' (')[0]}: <b>{Number(values[f.key] ?? 0)}</b> → <b>{Number(form[f.key])}</b><br /></span>
                    ))}
                </p>
                <p>Действие необратимо и не отобразится в истории начислений участника.</p>
            </div>,
            doSave,
            { confirmText: 'Изменить' }
        );
    };

    return (
        <Modal
            open
            kicker="Насоли соседу — участник"
            title="Редактирование балансов"
            onClose={onClose}
            footer={(
                <>
                    <button type="button" className="btn btn-ghost" onClick={onClose}>Отмена</button>
                    <button type="button" className="btn btn-primary" onClick={save} disabled={!allValid || changed.length === 0}>Сохранить</button>
                </>
            )}
        >
            <div className="sp-editnote">
                Значения устанавливаются как есть (не прибавляются). Изменение необратимо и не попадёт в историю начислений участника.
            </div>
            {FIELDS.map(f => (
                <LabeledInput key={f.key} label={f.label} type="number" step={f.step} value={form[f.key]} onChange={set(f.key)} />
            ))}
        </Modal>
    );
}

BalanceEditModal.propTypes = {
    stateId: PropTypes.number.isRequired,
    values: PropTypes.shape({
        balance: PropTypes.number,
        totalBalance: PropTypes.number,
        coinBalance: PropTypes.number
    }).isRequired,
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired
};
